import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'
import { Tenant } from '../../models'
import {
  AgentStateType,
  AGENT_ACTIVATING_STATES,
  AGENT_IN_PROGRESS_STATES,
  AGENT_FAST_POLL_STATES,
  AGENT_SLOW_POLL_STATES,
  AGENT_FAST_POLL_INTERVAL_MS,
  AGENT_SLOW_POLL_INTERVAL_MS
} from '../../models/AccountManagement'

export interface UserState {
  account: Tenant | null
  nexaAIEnabled: boolean
  nexaAIAgentState: AgentStateType | null
  nexaAIAgentName: string | null
}

const initialState: UserState = {
  account: null,
  nexaAIEnabled: false,
  nexaAIAgentState: null,
  nexaAIAgentName: null
}

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setAccountSettings: (state, action: PayloadAction<Tenant>) => {
      state.account = action.payload
    },
    setNexaAIStatus: (state, action: PayloadAction<{ enabled: boolean; agentName: string | null }>) => {
      state.nexaAIEnabled = action.payload.enabled
      state.nexaAIAgentName = action.payload.agentName
      state.nexaAIAgentState = null
    },
    setNexaAIAgentState: (state, action: PayloadAction<AgentStateType | null>) => {
      state.nexaAIAgentState = action.payload
    }
  }
})

export const { setAccountSettings, setNexaAIStatus, setNexaAIAgentState } = userSlice.actions

export const accountSettingsSelector = (state: RootState): Tenant | null => state.user.account

export const nexaAIEnabledSelector = (state: RootState): boolean => state.user.nexaAIEnabled

export const nexaAIAgentStateSelector = (state: RootState): AgentStateType | null => state.user.nexaAIAgentState

export const nexaAIActivatingSelector = (state: RootState): boolean =>
  AGENT_ACTIVATING_STATES.includes(state.user.nexaAIAgentState as AgentStateType)

export const nexaAIInProgressSelector = (state: RootState): boolean =>
  AGENT_IN_PROGRESS_STATES.includes(state.user.nexaAIAgentState as AgentStateType)

// How often to re-read the agent, or null when it has settled and needs no watching.
export const nexaAIPollIntervalSelector = (state: RootState): number | null => {
  const agentState = state.user.nexaAIAgentState as AgentStateType
  if (AGENT_FAST_POLL_STATES.includes(agentState)) return AGENT_FAST_POLL_INTERVAL_MS
  if (AGENT_SLOW_POLL_STATES.includes(agentState)) return AGENT_SLOW_POLL_INTERVAL_MS
  return null
}

export const nexaAIAgentNameSelector = (state: RootState): string | null => state.user.nexaAIAgentName

export default userSlice.reducer
